import { useState, useRef, useEffect } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { Eye, UserPlus, Plus } from 'react-feather'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useUser } from 'src/lib/contexts/UserContext'
import { permissionType } from 'src/lib/utils/constants'
import { classNames } from 'src/lib/utils/helpers'

interface InviteDropdownProps {
  onInvite: () => void
  onAddRecipient: () => void
}

const InviteDropdown: React.FC<InviteDropdownProps> = ({ onInvite, onAddRecipient }) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isRightAligned, setIsRightAligned] = useState(true)
  const { canAccess } = useUser()
  const canAddMember = canAccess(permissionType.ADD_MEMBER, 0)
  const { t } = useLocale()

  useEffect(() => {
    function handlePosition() {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect()
        const isOverflowing = rect.right > window.innerWidth
        setIsRightAligned(!isOverflowing)
      }
    }
    handlePosition()
    window.addEventListener('resize', handlePosition)
    return () => window.removeEventListener('resize', handlePosition)
  }, [])

  return (
    <div className='relative inline-block text-left '>
      <Menu as='div' className='relative'>
        <div>
          <Menu.Button
            id='invite-member-button'
            as='button'
            className='bg-primary-dark text-white px-4 py-1 rounded-lg flex items-center font-semibold gap-1'
          >
            <span>{t('invite')}</span>
            <Plus id='invite-member-button-icon' size={16} />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items
            ref={dropdownRef}
            className={`absolute mt-2 w-[26rem] bg-white rounded-lg shadow-lg ring-1 ring-black/5 focus:outline-none p-2 ${
              isRightAligned ? 'right-0' : 'left-0'
            } z-[9999]`}
            data-track-id='invite-dropdown-menu'
          >
            <div className='py-1'>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={onInvite}
                    className={classNames(
                      `flex flex-col  px-4 py-2 text-sm w-full text-left rounded-lg`,
                      active ? 'bg-gray-light' : '',
                      !canAddMember ? 'cursor-not-allowed opacity-50' : ''
                    )}
                    disabled={!canAddMember}
                  >
                    <div className='flex items-center text-coal-dark font-medium'>
                      <UserPlus className='w-5 h-5 mr-3 ' />
                      {t('members.invite_dashboard_user')}
                    </div>
                    <p className='pl-8 text-coal-light'>{t('members.define_users')}</p>
                  </button>
                )}
              </Menu.Item>

              <Menu.Item as='div' id='add-new-recipient'>
                {({ active }) => (
                  <button
                    id='add-new-recipient'
                    onClick={onAddRecipient}
                    className={`${
                      active ? 'bg-gray-100' : ''
                    } flex flex-col px-4 py-2 text-sm w-full text-left rounded-lg`}
                  >
                    <div className='flex items-center text-coal-dark font-medium'>
                      <Eye className='w-5 h-5 mr-3 text-gray-500' />
                      {t('members.add_new_recipient')}
                    </div>
                    <p className='pl-8 text-coal-light'>{t('members.add_new_recipient_description')}</p>
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  )
}

export default InviteDropdown
